import { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdContentCopy, MdSave } from 'react-icons/md';
import Card from 'components/card/Card';
import { TimeCombobox } from 'components/ui/TimeCombobox';
import Dropdown from 'components/ui/Dropdown';

// ─── Day helpers ──────────────────────────────────────────────────────────────

const DAYS = [
  { id: 'monday',    abbr: 'Lun' },
  { id: 'tuesday',   abbr: 'Mar' },
  { id: 'wednesday', abbr: 'Mié' },
  { id: 'thursday',  abbr: 'Jue' },
  { id: 'friday',    abbr: 'Vie' },
  { id: 'saturday',  abbr: 'Sáb' },
  { id: 'sunday',    abbr: 'Dom' },
] as const;

type DayId = typeof DAYS[number]['id'];

interface TimePeriod { startTime: string; endTime: string; }
interface DayConfig  { enabled: boolean; periods: TimePeriod[]; }

// Mock data — simula disponibilidad ya guardada para previsualizar el diseño
const MOCK_DAYS: Record<DayId, DayConfig> = {
  monday:    { enabled: true,  periods: [{ startTime: '08:00', endTime: '12:00' }, { startTime: '14:00', endTime: '17:00' }] },
  tuesday:   { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  wednesday: { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  thursday:  { enabled: true,  periods: [{ startTime: '08:00', endTime: '17:00' }] },
  friday:    { enabled: true,  periods: [{ startTime: '08:00', endTime: '13:00' }] },
  saturday:  { enabled: false, periods: [{ startTime: '08:00', endTime: '13:00' }] },
  sunday:    { enabled: false, periods: [{ startTime: '08:00', endTime: '13:00' }] },
};

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2).toString().padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

// ─── Remove-period icon (octagon minus) ──────────────────────────────────────

function OctMinusIcon() {
  return (
    <Box as="svg" xmlns="http://www.w3.org/2000/svg" width="16px" height="16px"
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.586 16.726A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2h6.624a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586z" />
      <path d="M8 12h8" />
    </Box>
  );
}

function AddCircleIcon() {
  return (
    <Box as="svg" xmlns="http://www.w3.org/2000/svg" width="18px" height="18px"
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8" /><path d="M12 8v8" />
    </Box>
  );
}

// ─── Preview component — diseño puro, sin backend ────────────────────────────

export default function AvailabilityConfigPreview() {
  const toast = useToast();

  const [days,     setDays]     = useState<Record<DayId, DayConfig>>(MOCK_DAYS);
  const [duration, setDuration] = useState(30);
  const [capacity, setCapacity] = useState(1);
  const [error,    setError]    = useState<string | null>(null);

  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor       = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const borderColor      = useColorModeValue('gray.100', 'whiteAlpha.100');

  const toggleDay = (dayId: DayId) =>
    setDays(prev => ({ ...prev, [dayId]: { ...prev[dayId], enabled: !prev[dayId].enabled } }));

  const addPeriod = (dayId: DayId) =>
    setDays(prev => ({
      ...prev,
      [dayId]: {
        enabled: true,
        periods: [...prev[dayId].periods, { startTime: '09:00', endTime: '17:00' }],
      },
    }));

  const removePeriod = (dayId: DayId, idx: number) => {
    const periods = days[dayId].periods;
    if (periods.length === 1) {
      toggleDay(dayId);
    } else {
      setDays(prev => ({
        ...prev,
        [dayId]: { ...prev[dayId], periods: prev[dayId].periods.filter((_, i) => i !== idx) },
      }));
    }
  };

  const updateTime = (dayId: DayId, idx: number, field: 'startTime' | 'endTime', value: string) =>
    setDays(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        periods: prev[dayId].periods.map((p, i) => i === idx ? { ...p, [field]: value } : p),
      },
    }));

  const copyToAll = (dayId: DayId) => {
    const src = days[dayId];
    setDays(prev => {
      const next = { ...prev };
      DAYS.forEach(d => {
        if (next[d.id].enabled && d.id !== dayId) {
          next[d.id] = { ...next[d.id], periods: [...src.periods] };
        }
      });
      return next;
    });
  };

  const handleSave = () => {
    setError(null);
    const activeDays = DAYS.filter(d => days[d.id].enabled);
    if (activeDays.length === 0) {
      setError('Selecciona al menos un día de atención.');
      return;
    }
    for (const day of activeDays) {
      for (const p of days[day.id].periods) {
        if (p.startTime >= p.endTime) {
          setError(`${day.abbr}: la hora de inicio debe ser anterior a la hora fin.`);
          return;
        }
      }
    }
    toast({
      title: 'Disponibilidad guardada (preview, sin backend)',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  return (
    <Box p="40px" minH="100vh" bg={useColorModeValue('secondaryGray.300', 'navy.900')}>
      <VStack spacing={4} align="stretch" maxW="640px" mx="auto">
        <Heading as="h1" size="lg">Vista Previa — Ajustes de Agenda</Heading>

        <Card>
          {/* Header */}
          <Flex align='center' justify='space-between' mb='24px'>
            <Box>
              <Text color={textColorPrimary} fontWeight='bold' fontSize='xl' mb='2px'>
                Ajustes de Agenda
              </Text>
              <Text color={mutedColor} fontSize='sm'>
                Configura tu jornada semanal y capacidad de atención paralela.
              </Text>
            </Box>
          </Flex>

          {/* Configuración de sesión */}
          <Box mb='24px'>
            <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase'
              letterSpacing='0.1em' mb='10px'>
              Configuración de Sesión
            </Text>
            <Flex gap='12px'>
              <Box flex={1}>
                <Text fontSize='xs' fontWeight='600' color={mutedColor} mb='6px'>
                  Duración de sesión
                </Text>
                <Dropdown
                  label={
                    duration === 15 ? '15 min' :
                    duration === 30 ? '30 min' :
                    duration === 45 ? '45 min' :
                    duration === 60 ? '1 h'    :
                    duration === 90 ? '1.5 h'  :
                    duration === 120 ? '2 h'   :
                    `${duration} min`
                  }
                  items={[
                    { label: '15 min', value: 15 },
                    { label: '30 min', value: 30 },
                    { label: '45 min', value: 45 },
                    { label: '1 h',    value: 60 },
                    { label: '1.5 h',  value: 90 },
                    { label: '2 h',    value: 120 },
                  ]}
                  onSelect={v => setDuration(Number(v))}
                  w='100%'
                  variant='outline'
                  size='sm'
                />
              </Box>

              <Box flex={1}>
                <Text fontSize='xs' fontWeight='600' color={mutedColor} mb='6px'>
                  Capacidad de Pacientes en Paralelo
                </Text>
                <Dropdown
                  label={capacity === 1 ? '1 paciente' : capacity === 2 ? '2 pacientes' : '3 pacientes'}
                  items={[
                    { label: '1 paciente',  value: 1 },
                    { label: '2 pacientes', value: 2 },
                    { label: '3 pacientes', value: 3 },
                  ]}
                  onSelect={v => setCapacity(Number(v))}
                  w='100%'
                  variant='outline'
                  size='sm'
                />
              </Box>
            </Flex>
          </Box>

          {/* Weekly schedule */}
          <Box mb='20px'>
            <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase'
              letterSpacing='0.1em' mb='4px'>
              Horario Semanal
            </Text>

            {DAYS.map(day => {
              const cfg = days[day.id];
              return (
                <Flex
                  key={day.id}
                  align='flex-start'
                  py='14px'
                  borderBottom='1px solid'
                  borderColor={borderColor}
                  gap='16px'
                  _last={{ borderBottom: 'none' }}
                >
                  {/* Day toggle + label */}
                  <Box
                    as='button'
                    type='button'
                    onClick={() => toggleDay(day.id)}
                    flexShrink={0}
                    w='48px'
                    pt='4px'
                  >
                    <Text
                      fontSize='sm'
                      fontWeight='700'
                      textTransform='uppercase'
                      letterSpacing='0.05em'
                      color={cfg.enabled ? textColorPrimary : mutedColor}
                      _hover={{ color: cfg.enabled ? 'red.400' : 'brand.500' }}
                      transition='color 0.15s'
                    >
                      {day.abbr}
                    </Text>
                  </Box>

                  {/* Periods */}
                  <Flex flex={1} direction='column' gap='6px'>
                    {!cfg.enabled ? (
                      <Text fontSize='sm' color={mutedColor} pt='4px'>No disponible</Text>
                    ) : (
                      cfg.periods.map((p, idx) => (
                        <Flex key={idx} align='center' gap='8px'>
                          <TimeCombobox
                            value={p.startTime}
                            onChange={v => updateTime(day.id, idx, 'startTime', v)}
                            options={TIME_OPTIONS}
                            listMaxH='170px'
                            flex={1}
                            w='0'
                          />
                          <Text fontSize='sm' color={mutedColor} flexShrink={0} userSelect='none'>–</Text>
                          <TimeCombobox
                            value={p.endTime}
                            onChange={v => updateTime(day.id, idx, 'endTime', v)}
                            options={TIME_OPTIONS}
                            listMaxH='170px'
                            flex={1}
                            w='0'
                          />
                          <Box
                            as='button'
                            type='button'
                            onClick={() => removePeriod(day.id, idx)}
                            color={mutedColor}
                            _hover={{ color: 'red.400' }}
                            transition='color 0.15s'
                            cursor='pointer'
                            bg='transparent'
                            border='none'
                            p='0'
                            display='flex'
                            alignItems='center'
                            flexShrink={0}
                            title='Quitar periodo'
                          >
                            <OctMinusIcon />
                          </Box>
                        </Flex>
                      ))
                    )}
                  </Flex>

                  {/* Actions */}
                  <Flex gap='6px' align='center' flexShrink={0} pt='2px'>
                    {cfg.enabled && (
                      <Box
                        as='button'
                        type='button'
                        onClick={() => copyToAll(day.id)}
                        color={mutedColor}
                        _hover={{ color: textColorPrimary }}
                        transition='color 0.15s'
                        cursor='pointer'
                        bg='transparent'
                        border='none'
                        p='0'
                        display='flex'
                        alignItems='center'
                        title='Copiar horario a todos los días activos'
                      >
                        <Icon as={MdContentCopy} w='15px' h='15px' />
                      </Box>
                    )}
                    <Box
                      as='button'
                      type='button'
                      onClick={() => cfg.enabled ? addPeriod(day.id) : toggleDay(day.id)}
                      color='brand.500'
                      _hover={{ color: 'brand.600' }}
                      transition='color 0.15s'
                      cursor='pointer'
                      bg='transparent'
                      border='none'
                      p='0'
                      display='flex'
                      alignItems='center'
                      title={cfg.enabled ? 'Añadir franja' : 'Activar día'}
                    >
                      <AddCircleIcon />
                    </Box>
                  </Flex>
                </Flex>
              );
            })}
          </Box>

          {error && (
            <Text fontSize='sm' color='red.500' mb='12px'>{error}</Text>
          )}

          <Button
            leftIcon={<Icon as={MdSave} />}
            colorScheme='brand'
            size='md'
            borderRadius='12px'
            onClick={handleSave}
          >
            Guardar disponibilidad
          </Button>
        </Card>
      </VStack>
    </Box>
  );
}
